import axios from 'axios';
import 'firebase/messaging';

import config from '../config';
import firebase from './firebase-service';
import { setPushNotificationsState, trackEvent } from './events-tracker-service';
import { playSound } from './sounds-service';
import { createAlert } from './alert-service';
import * as localStorageService from './local-storage-service';
import { getCurrentUserId } from './store-service';
import { initNotificationsService } from './electron-notification-service';
import { browserHistory } from './history';
import { getNotificationProps } from '../utils/notification';

let messaging;
try {
    messaging = firebase.messaging();
} catch (e) {
    messaging = null;
}

export const hasMessaging = () => ('Notification' in window) && Boolean(messaging);

let firebaseToken = localStorageService.get('firebaseSubscriptionToken');
let serviceInitialized = false;

const handleNotificationReceived = response => {
    if (!response.data || !response.data.payload) {
        return;
    }

    const notification = JSON.parse(response.data.payload);
    const notificationProps = getNotificationProps(notification);

    if (notificationProps && 'Notification' in window) {
        const notificationCard = new Notification(notification.message, {
            body: notificationProps.meta,
            icon: notificationProps.image || null,
            silent: true
        });

        playSound('notification');

        notificationCard.onclick = () => {
            redirect(notificationProps.url);
        }
    }
}

export const notificationsEnabled = () => ('Notification' in window) && Notification.permission === 'granted';

export const initBrowserNotificationService = async () => {
    if (!messaging) {
        return;
    }

    if (!serviceInitialized) {
        serviceInitialized = true;

        initNotificationsService({
            onTokenReceived: storeFirebaseToken,
            onTokenUpdated: storeFirebaseToken,
            onNotificationReceived: handleNotificationReceived
        });
    }

    if (!('Notification' in window)) {
        return;
    }

    switch (Notification.permission) {
        case 'denied':
            setPushNotificationsState(false);
            break;

        case 'granted':
            await deleteExtraneousToken();


            setPushNotificationsState(true);

            if (!firebaseToken) {
                getFirebaseToken();
            } else {
                subscribeTokenToTopic(firebaseToken, generateTopic());
            }
            break;

        default:
            // ESLint
    }
}

export const requestNotificationsPermission = () => {
    messaging && messaging.requestPermission()
        .then(getFirebaseToken)
        .then(() => trackEvent('PUSH_NOTIFICATIONS.ENABLE'))
        .catch(err => {
            createAlert('default', 'Unable to get permission to notify.');
        });
}

if (window.navigator && window.navigator.serviceWorker) {
    navigator.serviceWorker.addEventListener('message', event => {
        if (event.data.type) {
            switch (event.data.type) {
                case 'redirect':
                    redirect(event.data.url);
                    break;

                case 'notification':
                    playSound('notification');
                    break;

                default:
                    break;
            }
        }
    });
}

const redirect = url => {
    if (url.indexOf('#') === 0) {
        window.location.hash = url;
        return
    }

    browserHistory.push(url);
}

export const generateTopic = () => `${encodeURIComponent(encodeURIComponent(btoa(getCurrentUserId())))}_desktop`;

const deleteExtraneousToken = async () => {
    const existingToken = localStorageService.get('firebaseSubscriptionToken');
    const currentTopic = decodeURIComponent(generateTopic());

    if (existingToken) {
        try {
            const tokenDetails = await axios(`https://iid.googleapis.com/iid/info/${existingToken}?details=true`, {
                method: 'GET',
                headers: {
                    'Authorization': `key=${config.FIREBASE_CONFIG.mesagingAuthKey}`
                }
            });

            if (tokenDetails?.data?.rel?.topics) {
                Object.keys(tokenDetails.data.rel.topics).forEach(topic => {
                    if (topic !== currentTopic) {
                        axios(`https://iid.googleapis.com/iid/v1:batchRemove`, {
                            method: 'POST',
                            headers: {
                                'Authorization': `key=${config.FIREBASE_CONFIG.mesagingAuthKey}`
                            },
                            data: {
                                to: `/topics/${topic}`,
                                registration_tokens: [existingToken]
                            }
                        });
                    }
                });
            }
        } catch(e) {}
    }

    return true;
}

const subscribeTokenToTopic = (token, topic) => {
    axios(`https://iid.googleapis.com/iid/v1/${token}/rel/topics/${topic}`, {
        method: 'POST',
        headers: {
            'Authorization': `key=${config.FIREBASE_CONFIG.mesagingAuthKey}`
        }
    }).then(response => {
        if (response.status < 200 || response.status >= 400) {
            throw new Error(`Error subscribing to topic: ${response.status} - ${response.text()}`);
        } else {
            localStorageService.set('firebaseSubscriptionTopic', topic);
        }
    }).catch(error => {
        console.error(error);
    });
}

const storeFirebaseToken = token => {
    firebaseToken = token;
    localStorageService.set('firebaseSubscriptionToken', token);
    subscribeTokenToTopic(token, generateTopic());
}

const getFirebaseToken = () => {
    messaging.getToken()
        .then(currentToken => {
            if (currentToken) {
                storeFirebaseToken(currentToken);
            } else {
                // updateUIForPushPermissionRequired();
            }
        })
        .catch(err => {
            console.error('An error occurred while retrieving token. ', err);
            // showToken('Error retrieving Instance ID token. ', err);
        });

    // Callback fired if Instance ID token is updated.
    messaging.onTokenRefresh(() => {
        messaging.getToken()
            .then(refreshedToken => {
                storeFirebaseToken(refreshedToken);
            })
            .catch(function(err) {
                console.error('Unable to retrieve refreshed token ', err);
                // showToken('Unable to retrieve refreshed token ', err);
            });
    });
}
